import type { Hospital, Incident, Patient, SimState, Vehicle } from "./model";
import { patientPlan, profileFor, type ProfileId } from "./incidentProfiles";
import {
  createJourney,
  distanceKm,
  isAircraft,
  pendingRouting,
  type RoutingProvider,
} from "../providers/routing";
export const specialtyLabels = {
  general: "Notaufnahme",
  trauma: "Unfallchirurgie",
  cardiac: "Kardiologie",
  stroke: "Neurologie / Stroke",
  burn: "Verbrennungen",
  pediatric: "Kinderheilkunde",
};
export const careRequired = (p: Patient) =>
  p.severity === "critical" ? 240 : 120;
export function initializePatients(i: Incident, id: ProfileId) {
  const plan = patientPlan(id);
  i.patients ??= [];
  for (let n = i.patients.length; n < (plan?.count ?? 0); n++)
    i.patients.push({
      id: i.id + "-p" + (n + 1),
      label: "Patient " + (n + 1),
      severity: n < (plan?.critical ?? 0) ? "critical" : "stable",
      specialty: plan!.specialty,
      phase: "waiting",
      careSeconds: 0,
    });
  // A deteriorating individual keeps identity and accumulated treatment; critical care needs more time.
  for (let n = 0; n < Math.min(plan?.critical ?? 0, i.patients.length); n++)
    if (i.patients[n].phase === "waiting") {
      i.patients[n].severity = "critical";
      i.patients[n].specialty = plan!.specialty;
    }
}
export const allDelivered = (i: Incident) =>
  (i.patients ?? []).every((p) => p.phase === "delivered");
export const transportCapable = (v: Vehicle, p: Patient) =>
  ["RTW", "RTH", "ITH"].includes(v.kind) ||
  (v.kind === "KTW" && p.severity === "stable");
export function hospitalOptions(s: SimState, i: Incident, p: Patient) {
  return [...(s.hospitals ?? [])].sort((a, b) => {
    const score = (h: Hospital) =>
      distanceKm(i.location, h.location) +
      (h.specialties.includes(p.specialty) ? 0 : 200) +
      (h.emergencyConfirmed ? 0 : 20) +
      s.incidents
        .flatMap((i) => i.patients ?? [])
        .filter(
          (x) =>
            x.hospitalId === h.id &&
            ["transport", "handover"].includes(x.phase),
        ).length *
        5;
    return score(a) - score(b);
  });
}
function event(s: SimState, i: Incident, text: string) {
  i.timeline.push({
    id: "patient-" + s.now + "-" + i.timeline.length,
    at: s.now,
    type: "status",
    text,
  });
}
export function startTransport(
  s: SimState,
  i: Incident,
  p: Patient,
  v: Vehicle,
  h: Hospital,
  routing: RoutingProvider = pendingRouting,
) {
  if (
    i.phase === "abgeschlossen" ||
    p.phase !== "waiting" ||
    p.careSeconds < careRequired(p)
  )
    throw Error("Patient ist noch nicht transportbereit.");
  if (
    v.status !== 4 ||
    v.incidentId !== i.id ||
    v.journey ||
    v.transport ||
    !transportCapable(v, p)
  )
    throw Error(
      "Passendes freies Transportmittel muss an der Einsatzstelle sein.",
    );
  if (isAircraft(v.kind) && !h.helipad)
    throw Error(
      "Dieses Krankenhaus hat keinen hinterlegten Hubschrauberlandeplatz.",
    );
  if (
    s.speechRequests.some((r) => r.vehicleId === v.id && r.phase === "reported")
  )
    throw Error("Zuerst die laufende Funkmeldung bestätigen.");
  v.transport = { patientId: p.id, hospitalId: h.id };
  v.journey = createJourney(
    v,
    h.location,
    s.now,
    false,
    s.stations.find((w) => w.id === v.stationId),
    routing,
  );
  v.status = 7;
  p.phase = "transport";
  p.vehicleId = v.id;
  p.hospitalId = h.id;
  s.speechRequests = s.speechRequests.filter((r) => r.vehicleId !== v.id);
  event(
    s,
    i,
    v.name +
      " übernimmt " +
      p.label +
      " · Status 7 · " +
      (isAircraft(v.kind) ? "Flug" : "Transport") +
      " nach " +
      h.name +
      ".",
  );
}
export function transportArrival(s: SimState, v: Vehicle) {
  const i = s.incidents.find((i) => i.id === v.incidentId);
  const p = i?.patients?.find((p) => p.id === v.transport?.patientId);
  if (!i || !p || !v.transport) throw Error("Patientenreferenz fehlt.");
  v.status = 8;
  v.journey = null;
  v.transport.handoverUntil =
    s.now + (p.severity === "critical" ? 300 : 180) * 1000;
  p.phase = "handover";
  event(
    s,
    i,
    v.name + " · Status 8 · " + p.label + " am Krankenhaus; Übergabe läuft.",
  );
}
export function advancePatients(s: SimState, seconds: number) {
  for (const v of s.vehicles) {
    if (
      v.status !== 8 ||
      !v.transport?.handoverUntil ||
      s.now < v.transport.handoverUntil
    )
      continue;
    const i = s.incidents.find((i) => i.id === v.incidentId)!;
    const p = i.patients!.find((p) => p.id === v.transport!.patientId)!;
    p.phase = "delivered";
    p.deliveredAt = s.now;
    event(
      s,
      i,
      p.label +
        " an " +
        (s.hospitals?.find((h) => h.id === p.hospitalId)?.name ??
          "Krankenhaus") +
        " übergeben. " +
        v.name +
        " wieder frei.",
    );
    delete v.transport;
    v.status = 1;
    v.incidentId = null;
    v.journey = createJourney(
      v,
      s.stations.find((w) => w.id === v.stationId)!.location,
      s.now,
      true,
    );
  }
  for (const i of s.incidents.filter(
    (i) => i.phase !== "abgeschlossen" && i.patients?.length,
  )) {
    const scene = s.vehicles.filter(
      (v) =>
        v.incidentId === i.id && v.status === 4 && !v.journey && !v.transport,
    );
    const transport = scene.filter((v) =>
      ["RTW", "KTW", "RTH", "ITH"].includes(v.kind),
    );
    let doctorTeams = scene.filter((v) =>
      ["NEF", "RTH", "ITH"].includes(v.kind),
    ).length;
    const waiting = i
      .patients!.filter((p) => p.phase === "waiting")
      .sort(
        (a, b) =>
          Number(b.severity === "critical") - Number(a.severity === "critical"),
      );
    // One treatment team per patient. Await technical rescue at entrapped/fire/remote scenes.
    const rescueNeeded =
      ["brand", "vu", "berg", "wasser", "unwetter"].includes(i.type) &&
      Object.keys(profileFor(i).needs).some((k) =>
        ["HLF", "LF", "RW", "Bergrettung", "GW-Wasser", "RTB"].includes(k),
      );
    const rescueReady =
      !rescueNeeded ||
      i.workSeconds >= Math.min(120, profileFor(i).workSeconds);
    let teams = [...transport];
    for (const p of waiting) {
      const teamIndex = teams.findIndex((v) => transportCapable(v, p));
      if (teamIndex < 0) continue;
      const [team] = teams.splice(teamIndex, 1);
      if (!rescueReady) continue;
      if (p.careSeconds < careRequired(p)) {
        if (p.severity === "critical") {
          if (doctorTeams <= 0) continue;
          doctorTeams--;
        }
        p.careSeconds += seconds;
      }
      if (p.careSeconds < careRequired(p)) continue;
      const choices = hospitalOptions(s, i, p);
      const ground = transport.find(
        (v) =>
          v.status === 4 &&
          !v.transport &&
          !isAircraft(v.kind) &&
          transportCapable(v, p),
      );
      const air = transport.find(
        (v) => v.status === 4 && !v.transport && isAircraft(v.kind),
      );
      const preferAir =
        p.severity === "critical" &&
        (i.type === "berg" ||
          (choices[0] && distanceKm(i.location, choices[0].location) > 25));
      const v = (preferAir ? (air ?? ground) : (ground ?? air)) ?? team;
      const h = choices.find((h) => !isAircraft(v.kind) || h.helipad);
      if (
        !h ||
        v.status !== 4 ||
        s.speechRequests.some(
          (r) => r.vehicleId === v.id && r.phase === "reported",
        )
      )
        continue;
      startTransport(s, i, p, v, h);
    }
  }
}
